import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44, supabase } from '@/api/base44Client';
import { useCallback, useEffect, useRef } from 'react';
import { useSession } from '@/lib/SessionContext';
import { dispatchNotification } from '@/lib/notificationEngine';

const ENTITY = 'ClientPO';

/**
 * useClientPOs — centralized hook for Client PO data
 */
export function useClientPOs() {
  const qc = useQueryClient();
  const { member } = useSession();

  // Fetch from Supabase
  const { data: clientPOs = [], isLoading, refetch } = useQuery({
    queryKey: ['client-pos', member?.id],
    queryFn: async () => {
      const data = await base44.entities[ENTITY].list('-created_date');
      const validData = data.filter(po => {
        if (po.deleted_at) return false;
        if (po._isManualReceivable) return false;
        if (po.is_custom_finance && member?.role === 'marketing') return false;
        return true;
      });
      
      return validData;
    },
    staleTime: 30000,
  });

  // Create — with optimistic update & Smart Notification Dispatch
  const createMut = useMutation({
    mutationFn: async (data) => {
      const created = await base44.entities[ENTITY].create(data);

      // Trigger Smart Notification Dispatch
      try {
        const actor = member || { id: 'system', full_name: 'Sistem Satala', role: 'system' };
        const pic = {
          id: data.pic_id || '',
          name: data.pic_name || data.pic || data.pic_marketing || '',
        };
        const invNo = created.id || data.id || data.order_number || 'INV-Baru';
        const brand = data.brand_name || data.client || data.customerName || 'Klien';

        dispatchNotification({
          eventId: 'invoice_created',
          title: 'Invoice Baru Dibuat',
          body: `Invoice ${invNo} untuk ${brand} telah dibuat oleh ${actor.full_name}.`,
          badgeText: 'FINANCE',
          actor: { id: actor.id, full_name: actor.full_name, role: actor.role },
          targetPic: pic,
          meta: {
            invoice_id: invNo,
            brand_name: brand,
            total_amount: data.total_amount || data.grand_total || 0,
          },
          actionUrl: `/office/finance/invoices`,
        });
      } catch (err) {
        console.warn('[useClientPOs] Gagal dispatch notifikasi invoice:', err);
      }

      return created;
    },
    onMutate: async (data) => {
      await qc.cancelQueries({ queryKey: ['client-pos'] });
      const prev = qc.getQueryData(['client-pos']);
      qc.setQueryData(['client-pos'], (old = []) => [{ ...data, created_date: new Date().toISOString(), updated_date: new Date().toISOString() }, ...old]);
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(['client-pos'], ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['client-pos'] });
      qc.invalidateQueries({ queryKey: ['all-client-pos-ranking'] });
    },
  });

  // Update — with optimistic update for instant UI
  const updateMut = useMutation({
    mutationFn: ({ currentId, updates }) => base44.entities[ENTITY].update(currentId, updates),
    onMutate: async ({ currentId, updates }) => {
      // Cancel outgoing refetches
      await qc.cancelQueries({ queryKey: ['client-pos'] });
      // Snapshot previous
      const prev = qc.getQueryData(['client-pos']);
      // Optimistically update
      qc.setQueryData(['client-pos'], (old = []) =>
        old.map(po => po.id === currentId ? { ...po, ...updates, updated_date: new Date().toISOString() } : po)
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      // Roll back on error
      if (ctx?.prev) qc.setQueryData(['client-pos'], ctx.prev);
    },
    onSettled: () => {
      // Refetch in background for consistency, fire and forget
      qc.invalidateQueries({ queryKey: ['client-pos'] });
      qc.invalidateQueries({ queryKey: ['all-client-pos-ranking'] });
    },
  });

  // Soft Delete (move to trash)
  const deleteMut = useMutation({
    mutationFn: (payload) => {
      const id = typeof payload === 'string' ? payload : payload.id;
      const deleted_by = typeof payload === 'object' ? payload.deleted_by : undefined;
      return base44.entities[ENTITY].hardDelete(id, deleted_by);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['client-pos'] });
      qc.invalidateQueries({ queryKey: ['all-client-pos-ranking'] });
    },
  });

  // Hard Delete (permanent, from trash)
  const hardDeleteMut = useMutation({
    mutationFn: (id) => base44.entities[ENTITY].hardDelete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['client-pos'] });
      qc.invalidateQueries({ queryKey: ['all-client-pos-ranking'] });
    },
  });

  // Helper: update a specific PO by id
  const updatePO = useCallback((id, updates) => {
    return updateMut.mutateAsync({ currentId: id, updates });
  }, [updateMut]);

  // Helper: find PO by id
  const findPO = useCallback((id) => {
    return clientPOs.find(po => po.id === id) || null;
  }, [clientPOs]);

  // Helper: filter POs
  const filterPOs = useCallback((filterFn) => {
    return clientPOs.filter(filterFn);
  }, [clientPOs]);

  // ===== TRASH SYSTEM =====
  // Fetch soft-deleted items (trash)
  const { data: trashPOs = [], refetch: refetchTrash } = useQuery({
    queryKey: ['client-pos-trash', member?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from(ENTITY)
        .select('*')
        .not('data->deleted_at', 'is', null);
      if (error) return [];
      const mapped = (data || []).map(row => ({
        ...row.data,
        id: row.id,
        created_date: row.created_date,
        updated_date: row.updated_date,
      }));
      
      return mapped;
    },
    staleTime: 10000,
  });

  // Restore from trash
  const restoreMut = useMutation({
    mutationFn: (id) => base44.entities[ENTITY].restore(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['client-pos'] });
      qc.invalidateQueries({ queryKey: ['client-pos-trash'] });
    },
  });

  // Auto-cleanup: hard delete items in trash older than 14 days
  const autoCleanupDone = useRef(false);
  useEffect(() => {
    if (autoCleanupDone.current || trashPOs.length === 0) return;
    autoCleanupDone.current = true;

    const now = Date.now();
    const FOURTEEN_DAYS = 14 * 24 * 60 * 60 * 1000;

    const expiredItems = trashPOs.filter(po => {
      if (!po.deleted_at) return false;
      const deletedTime = new Date(po.deleted_at).getTime();
      return (now - deletedTime) > FOURTEEN_DAYS;
    });

    if (expiredItems.length > 0) {
      console.log(`[Trash Cleanup] Auto-deleting ${expiredItems.length} item(s) older than 14 days...`);
      Promise.all(expiredItems.map(po => base44.entities[ENTITY].hardDelete(po.id)))
        .then(() => {
          console.log('[Trash Cleanup] Done!');
          qc.invalidateQueries({ queryKey: ['client-pos-trash'] });
        })
        .catch(err => console.warn('[Trash Cleanup] Error:', err));
    }
  }, [trashPOs, qc]);

  return {
    clientPOs,
    isLoading,
    refetch,
    createPO: createMut.mutateAsync,
    updatePO,
    deletePO: deleteMut.mutateAsync,            // soft delete → trash
    hardDeletePO: hardDeleteMut.mutateAsync,     // permanent delete
    restorePO: restoreMut.mutateAsync,           // restore from trash
    trashPOs,                                     // items in trash
    refetchTrash,
    findPO,
    filterPOs,
    // Raw mutations for fine-grained control
    createMut,
    updateMut,
    deleteMut,
    hardDeleteMut,
  };
}
